import { Particles, blur, clamp, requestMedia } from "../shared";

const MODES = [];
for (let n = 0; n <= 11; n++) for (let m = n; m <= 11; m++) if (n + m > 0 && !(n === 0 && m === 1)) MODES.push({ n, m, k: n * n + m * m });
MODES.sort((a, b) => a.k - b.k);
const MEDIA = { sand: [1, 0.82, 0.5], salt: [0.95, 0.98, 1], water: [0, 0.94, 1] };

class Cymatics {
  constructor(ctx) {
    this.ctx = ctx; this.writesColors = true; this.f = ctx.params.freq; this.status = "Silent drive"; this.level = 0;
    this.grains = new Particles(ctx.overlay, ctx.lowPower ? 1800 : 7000, 0xffd28a, ctx.lowPower ? 0.07 : 0.05);
    this.alloc(); this.scatter();
  }
  alloc() { const n = this.ctx.count; this.U = new Float32Array(n); this.dens = new Float32Array(n); this.lastF = -1; }
  scatter() {
    const g = this.grains, h = this.ctx.half;
    for (let i = 0; i < g.count; i++) { g.pos[i * 3] = (Math.random() * 2 - 1) * h * 0.98; g.pos[i * 3 + 2] = (Math.random() * 2 - 1) * h * 0.98; }
  }
  base() { return 14 * this.ctx.params.stiffness; }
  resonance() {
    const Q = this.ctx.params.Q, b = this.base();
    return MODES.map((md) => { const f0 = b * md.k, x = (this.f - f0) / (f0 / Q); return { ...md, f0, w: 1 / (1 + x * x) }; })
      .sort((a, c) => c.w - a.w).slice(0, 2);
  }
  shape() {
    const { N, px, half } = this.ctx, U = this.U, P = this.ctx.params;
    this.res = this.resonance();
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      const x = (px[c] + half) / (2 * half), y = (px[r] + half) / (2 * half), i = r * N + c;
      let u = 0;
      if (P.medium === "water") {
        const dx = x - 0.5, dy = y - 0.5, rr = Math.hypot(dx, dy) * 2, th = Math.atan2(dy, dx), k = Math.sqrt(this.f / this.base()) * 2.2;
        u = rr > 1 ? 0 : Math.cos(k * Math.PI * rr) * Math.cos(Math.round(k / 2) * th) * (1 - rr * 0.35);
      } else for (const md of this.res) {
        const s = (md.n + md.m) % 2 === 0 ? 1 : -1, pn = md.n * Math.PI, pm = md.m * Math.PI;
        u += md.w * (Math.cos(pn * x) * Math.cos(pm * y) + s * Math.cos(pm * x) * Math.cos(pn * y)) * 0.5;
      }
      U[i] = u;
    }
    this.lastF = this.f; this.lastMedium = P.medium;
  }
  async startAudio(kind) {
    this.stopAudio(false);
    const AC = window.AudioContext || window.webkitAudioContext; this.ac = new AC();
    if (kind === "mic") {
      this.status = "Requesting mic…";
      try { this.stream = await requestMedia({ audio: true }); }
      catch (e) { this.stopAudio(); throw e; }
      this.an = this.ac.createAnalyser(); this.an.fftSize = 4096; this.ac.createMediaStreamSource(this.stream).connect(this.an);
      this.bins = new Uint8Array(this.an.frequencyBinCount); this.status = "Microphone";
      return "Listening — sing or play a note near the mic";
    }
    const P = this.ctx.params; this.osc = this.ac.createOscillator(); this.gain = this.ac.createGain();
    this.osc.type = P.wave; this.osc.frequency.value = this.f; this.gain.gain.value = P.volume * 0.4;
    this.osc.connect(this.gain).connect(this.ac.destination); this.osc.start(); this.status = "Audible tone";
  }
  stopAudio(resetUI = true) {
    if (resetUI && this.ctx.params.play) this.ctx.setUI("play", false);
    this.stream?.getTracks().forEach((t) => t.stop()); try { this.osc?.stop(); } catch { /* already stopped */ }
    this.ac?.close?.(); this.ac = this.an = this.osc = this.gain = this.stream = null; this.status = "Silent drive";
  }
  listen() {
    if (!this.an) return;
    this.an.getByteFrequencyData(this.bins);
    let best = 0, bi = 0, sum = 0; const hz = this.ac.sampleRate / this.an.fftSize;
    for (let i = 3; i < this.bins.length * 0.4; i++) { sum += this.bins[i]; if (this.bins[i] > best) { best = this.bins[i]; bi = i; } }
    this.level = best / 255;
    if (best > 60) this.f = clamp(bi * hz, 60, 3000);
  }
  step(dt, t) {
    const P = this.ctx.params, { N, sp, half, target, colors } = this.ctx;
    if (this.sweep) { this.f = 80 + ((t - this.sweep) / 24) * 2300; if (this.f > 2400) this.sweep = null; this.osc?.frequency.setTargetAtTime(this.f, this.ac.currentTime, 0.03); if (!this.sweep || (this.tick = (this.tick || 0) + 1) % 20 === 0) this.syncFreq(); }
    this.listen();
    if (Math.abs(this.f - this.lastF) > 0.5 || P.medium !== this.lastMedium) this.shape();
    const drive = this.an ? this.level * 1.4 : P.excite || this.osc ? 1 : 0;
    const strength = drive * (P.medium === "water" ? 1 : this.res[0].w) * this.ctx.g.force, U = this.U, D = this.dens;
    const g = this.grains, water = P.medium === "water"; g.visible = !water;
    D.fill(0);
    if (!water) {
      const kick = P.kick * strength * dt * 2.6, cell = (x) => clamp(Math.floor((x + half) / sp), 0, N - 1);
      for (let i = 0; i < g.count; i++) {
        let x = g.pos[i * 3], z = g.pos[i * 3 + 2];
        const c = cell(x), r = cell(z), id = r * N + c, a = Math.abs(U[id]);
        const gx = Math.abs(U[r * N + Math.min(N - 1, c + 1)]) - Math.abs(U[r * N + Math.max(0, c - 1)]);
        const gz = Math.abs(U[Math.min(N - 1, r + 1) * N + c]) - Math.abs(U[Math.max(0, r - 1) * N + c]);
        x += (Math.random() - 0.5) * a * kick * 2 - gx * kick * 0.8; z += (Math.random() - 0.5) * a * kick * 2 - gz * kick * 0.8;
        if (this.agitate && Math.hypot(x - this.agitate.x, z - this.agitate.z) < 1.2) { x += (Math.random() - 0.5) * 0.4; z += (Math.random() - 0.5) * 0.4; }
        x = clamp(x, -half * 0.99, half * 0.99); z = clamp(z, -half * 0.99, half * 0.99);
        g.pos[i * 3] = x; g.pos[i * 3 + 2] = z; D[cell(z) * N + cell(x)] += 1;
      }
    }
    const dens = water ? D : blur(D, N, 1), scale = (N * N) / g.count, shake = Math.sin(t * 40) * 0.06 * strength, tint = MEDIA[P.medium];
    let settled = 0;
    for (let i = 0; i < N * N; i++) {
      const s = Math.min(1, dens[i] * scale * 0.25);
      if (Math.abs(U[i]) < 0.12) settled += dens[i];
      const h = water ? U[i] * Math.sin(t * 6) * 0.85 * strength - 0.1 : -0.85 + s * P.pile * 1.6 + U[i] * shake;
      target[i] = clamp(h, -1, 1);
      const a = water ? Math.min(1, Math.abs(target[i] + 0.1)) * 0.8 : s * 0.9;
      colors[i * 3] = 0.55 + (tint[0] - 0.55) * a; colors[i * 3 + 1] = 0.6 + (tint[1] - 0.6) * a; colors[i * 3 + 2] = 0.68 + (tint[2] - 0.68) * a;
    }
    this.settled = water ? 0 : settled / g.count;
    if (!water) for (let i = 0; i < g.count; i++) g.pos[i * 3 + 1] = this.ctx.sampleY(g.pos[i * 3], g.pos[i * 3 + 2]) + 0.04;
    g.obj.material.color.setRGB(...tint); g.update();
  }
  pointer(e) { this.agitate = e.type === "up" ? null : { x: e.x, z: e.z }; }
  onParams(key) {
    const P = this.ctx.params;
    if (key === "freq") { this.f = P.freq; this.sweep = null; this.osc?.frequency.setTargetAtTime(this.f, this.ac.currentTime, 0.03); }
    if (key === "stiffness" || key === "Q") this.lastF = -1;
    if (key === "wave" && this.osc) this.osc.type = P.wave;
    if (key === "volume" && this.gain) this.gain.gain.value = P.volume * 0.4;
    if (key === "play") P.play ? this.startAudio("tone") : this.osc && this.stopAudio();
  }
  jump(dir) {
    const b = this.base(), fs = MODES.map((m) => b * m.k).filter((f) => f >= 60 && f <= 3000);
    const next = dir > 0 ? fs.find((f) => f > this.f + 1) : [...fs].reverse().find((f) => f < this.f - 1);
    if (next) { this.f = next; this.sweep = null; this.osc?.frequency.setTargetAtTime(this.f, this.ac.currentTime, 0.03); this.syncFreq(); }
  }
  async action(id) {
    if (id === "next" || id === "prev") { this.jump(id === "next" ? 1 : -1); return; }
    if (id === "sweep") { this.sweep = this.ctx.simTime?.() ?? 0; return "Sweeping 80 Hz → 2.4 kHz"; }
    if (id === "scatter") { this.ctx.pushHistory(); this.scatter(); return; }
    if (id === "mic") return this.startAudio("mic");
    if (id === "stopAudio") { this.stopAudio(); return "Audio stopped"; }
  }
  resize() { this.alloc(); }
  snapshot() { return { pos: this.grains.pos.slice(), f: this.f }; }
  syncFreq() { this.ctx.setUI("freq", clamp(Math.round(this.f), 60, 3000)); }
  restore(s) { this.grains.pos.set(s.pos); this.f = s.f; this.syncFreq(); }
  clear() { this.scatter(); }
  telemetry() {
    const md = this.res?.[0];
    return [["Frequency", `${Math.round(this.f)} Hz`], ["Mode (n,m)", md && this.ctx.params.medium !== "water" ? `${md.n},${md.m}` : "radial"], ["Resonance", md ? `${Math.round(md.w * 100)}%` : "—"].map((v, k) => (k === 1 && this.ctx.params.medium === "water" ? "—" : v)), ["Source", this.status], ["On nodes", `${Math.round((this.settled || 0) * 100)}%`]];
  }
  dispose() { this.stopAudio(); this.grains.dispose(); }
}

const cymatics = {
  id: "cymatics-lab", name: "Cymatics Lab", icon: "Disc3", rest: -0.85,
  desc: "A vibrating Chladni plate covered in sand. Each frequency excites a standing-wave mode; grains are shaken off the moving antinodes and pile up on the still nodal lines, drawing the sound. Tune the frequency, jump between resonances, turn on the audible tone or sing into the mic. Drag on the plate to stir the grains.",
  math: "f(n,m) ∝ n² + m²   ·   u = cos nπx·cos mπy ± cos mπx·cos nπy",
  params: [
    { key: "medium", label: "Medium", type: "select", options: [["sand", "Sand"], ["salt", "Fine salt"], ["water", "Water dish"]], def: "sand" },
    { key: "freq", label: "Drive frequency (Hz)", type: "range", min: 60, max: 3000, step: 1, def: 476, testid: "slider-cymatics-frequency" },
    { key: "excite", label: "Vibrate plate (silent)", type: "toggle", def: true },
    { key: "play", label: "Audible tone", type: "toggle", def: false, testid: "toggle-cymatics-audible" },
    { key: "wave", label: "Tone waveform", type: "select", options: [["sine", "Sine"], ["triangle", "Triangle"], ["square", "Square"], ["sawtooth", "Saw"]], def: "sine" },
    { key: "volume", label: "Tone volume", type: "range", min: 0, max: 1, step: 0.01, def: 0.3 },
    { key: "Q", label: "Plate resonance Q", type: "range", min: 4, max: 60, step: 1, def: 22 },
    { key: "stiffness", label: "Plate stiffness", type: "range", min: 0.5, max: 2, step: 0.05, def: 1 },
    { key: "kick", label: "Agitation", type: "range", min: 0.2, max: 3, step: 0.05, def: 1.2 },
    { key: "pile", label: "Sand pile height", type: "range", min: 0.1, max: 1.5, step: 0.05, def: 0.8 },
  ],
  actions: [
    { id: "prev", label: "◂ Prev resonance", testid: "cymatics-prev" }, { id: "next", label: "Next resonance ▸", testid: "cymatics-next" },
    { id: "sweep", label: "Frequency sweep" }, { id: "scatter", label: "Scatter grains" },
    { id: "mic", label: "Listen via mic", testid: "cymatics-mic" }, { id: "stopAudio", label: "Stop audio" },
  ],
  create: (ctx) => new Cymatics(ctx),
};
export default cymatics;
